/**
 * permissionDenied.ts — the one shape a permission refusal has on the client,
 * and the one channel it travels on.
 *
 * Owns: the `PermissionDenial` type mirroring the backend's
 *       `PERMISSION_DENIED` details envelope, the reader that pulls it off
 *       an unknown caught value, and a React-free event channel so the
 *       axios interceptor can announce a denial without importing a hook.
 * Used by: `services/apiClient.ts` (announces), `contexts/ToastContext`'s
 *       listener (shows the one canonical toast), and any surface that wants
 *       to render `<PermissionDenied>` instead of an empty table.
 * Depends on: nothing. Pure types + a window event.
 *
 * WHY A CHANNEL AND NOT A DIRECT CALL. The interceptor is a module, not a
 * component, so it cannot call `useToast()`. Before this, that gap is exactly
 * why one server condition produced five different user experiences — every
 * catch block invented its own. A single event, emitted once at the seam every
 * response already passes through, means the app has one answer for "you are
 * not allowed to do that" and no surface has to remember to write it.
 */

export interface DeniedPermission {
  /** `action:send_newsletter` — the key the server demanded. */
  key: string;
  /** What Settings > Users & Roles calls it. */
  label: string;
  /** The catalog heading it lives under. */
  feature: string;
  /** Click-path an admin follows to grant it. */
  grantPath: string;
  /** False when the key is enforced in code but missing from the catalog. */
  catalogued: boolean;
}

export interface PermissionDenial {
  /** The human sentence the server wrote. Already names the label + path. */
  message: string;
  /** Every key that would have unblocked the request (usually one). */
  permissions: DeniedPermission[];
  /** Convenience: the first/primary permission. */
  primary: DeniedPermission;
  /** Where it happened, for the toast's context and dev-mode captures. */
  method: string;
  url: string;
}

export const PERMISSION_DENIED_CODE = 'PERMISSION_DENIED';
export const PERMISSION_DENIED_EVENT = 'bsg:permission-denied';

/** Property the interceptor stamps on the axios error so any `catch` can read
 *  the denial without re-parsing the response body. */
export const DENIAL_PROP = '__permissionDenial';

/**
 * Read a denial off an unknown caught value. Returns null for every other
 * failure, so callers can branch without narrowing axios types themselves.
 */
export function getPermissionDenial(err: unknown): PermissionDenial | null {
  if (!err || typeof err !== 'object') return null;
  const direct = (err as Record<string, unknown>)[DENIAL_PROP];
  if (direct && typeof direct === 'object') return direct as PermissionDenial;

  // Fall back to parsing the envelope: a caller may hold the response body
  // rather than the axios error (some services unwrap before rethrowing).
  const body = (err as any)?.response?.data ?? err;
  if (!body || typeof body !== 'object') return null;
  if ((body as any).code !== PERMISSION_DENIED_CODE) return null;
  return normalizeDenial(body, (err as any)?.config?.method, (err as any)?.config?.url);
}

/** Build the client-side shape from a raw error envelope. Tolerant by design:
 *  an older server that sends only `error` still produces a usable denial. */
export function normalizeDenial(body: any, method?: string, url?: string): PermissionDenial {
  const details = body?.details ?? {};
  const list: DeniedPermission[] = Array.isArray(details.permissions) && details.permissions.length > 0
    ? details.permissions
    : [{
      key: details.key ?? 'unknown',
      label: details.label ?? 'this action',
      feature: details.feature ?? 'Uncategorised',
      grantPath: details.grantPath ?? 'Settings > Users & Roles',
      catalogued: details.catalogued !== false,
    }];
  return {
    message: typeof body?.error === 'string' && body.error
      ? body.error
      : "You don't have permission to do that.",
    permissions: list,
    primary: list[0],
    method: (method || 'get').toUpperCase(),
    url: url || '',
  };
}

/** Announce a denial to whoever is listening (the toast, dev mode). */
export function emitPermissionDenied(denial: PermissionDenial): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<PermissionDenial>(PERMISSION_DENIED_EVENT, { detail: denial }));
}

/** Subscribe. Returns an unsubscribe function. */
export function onPermissionDenied(handler: (denial: PermissionDenial) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const wrapped = (e: Event) => handler((e as CustomEvent<PermissionDenial>).detail);
  window.addEventListener(PERMISSION_DENIED_EVENT, wrapped);
  return () => window.removeEventListener(PERMISSION_DENIED_EVENT, wrapped);
}
